import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PiggyBank } from "lucide-react";
import { toast } from "sonner";
import { CelebrationOverlay } from "@/components/brand/CoinMascot";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useHousehold } from "@/context/HouseholdContext";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const monthLabel = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

export const AutoSave = ({ goals }) => {
  const { active } = useHousehold();
  const { money } = useFormat();
  const qc = useQueryClient();
  const open = goals.filter((g) => !g.reached);
  const { data: s } = useQuery({ queryKey: ["goals", active?.id, "sweep"], queryFn: () => api.get(`/households/${active.id}/goals/sweep`).then((r) => r.data), enabled: !!active });
  const [gid, setGid] = useState("");
  const [party, setParty] = useState(null);
  const goalId = gid || s?.auto_goal_id || open[0]?.id || "";
  const refresh = () => qc.invalidateQueries({ queryKey: ["goals", active.id] });

  useEffect(() => {
    if (s?.pending?.length && !party) { setParty(s.pending[0]); }
  }, [s, party]);

  if (!s || !open.length) return null;
  const close = async () => {
    if (party?.auto) { await api.post(`/households/${active.id}/goals/sweep/${party.id}/seen`).catch(() => {}); }
    setParty(null);
    refresh();
  };
  const sweep = async () => {
    try {
      const { data: r } = await api.post(`/households/${active.id}/goals/sweep`, { goal_id: goalId, amount: s.proposal });
      setParty({ goal_name: r.goal.name, amount: r.amount, just_reached: r.just_reached, auto: false });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (on) => {
    try {
      await api.put(`/households/${active.id}/goals/${goalId}/auto-sweep`, { enabled: on });
      toast.success(on ? "Virement automatique activé chaque fin de mois" : "Virement automatique désactivé");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-border bg-card/70 p-4" data-testid="autosave-card">
      <div className="flex items-start gap-3">
        <span className="tone-chip tone-mint flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"><PiggyBank className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-semibold">Épargner le reste à vivre</div>
          <div className="text-xs text-muted-foreground" data-testid="autosave-rav">
            {s.proposal > 0 ? <>Prévu en fin de mois : <b className="num text-foreground">{money(s.proposal)}</b> · J-{s.days_left}</> : "Pas de reste à vivre prévu ce mois-ci."}
          </div>
        </div>
      </div>
      <Select value={goalId} onValueChange={setGid}><SelectTrigger className="h-9" data-testid="autosave-goal-select"><SelectValue placeholder="Objectif" /></SelectTrigger>
        <SelectContent>{open.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent></Select>
      {s.done_this_month ? <p className="text-xs font-semibold text-success" data-testid="autosave-done">{money(s.done_this_month.amount)} mis de côté ce mois-ci vers « {s.done_this_month.goal_name} »</p>
        : s.proposal > 0 && <Button size="sm" className="w-full rounded-full" disabled={!goalId} onClick={sweep} data-testid="autosave-sweep-button">Virer {money(s.proposal)} maintenant</Button>}
      <label className="flex items-center justify-between gap-2 text-xs">
        <span>Automatique chaque fin de mois</span>
        <Switch checked={!!s.auto_goal_id && s.auto_goal_id === goalId} onCheckedChange={toggle} disabled={!goalId} data-testid="autosave-auto-switch" />
      </label>
      <p className="text-[11px] text-muted-foreground">Virement simulé vers votre objectif : aucun mouvement bancaire réel.</p>
      <CelebrationOverlay open={!!party} onClose={close} cta="Super !" title={party?.just_reached ? "Objectif atteint !" : "Bien joué !"}
        text={party ? `${money(party.amount)} ${party.auto ? `de ${monthLabel(party.month)} ont été virés automatiquement` : "ont été mis de côté"} vers « ${party.goal_name} ».` : ""} />
    </div>
  );
};
